// Pages/inventario/components/CampoEtiqueta.tsx
//
// Campo de texto libre del inventario (nombre, marca, color, material…) con
// sugerencias y AVISO DE PARECIDO — round 2026-08-06.
//
// El problema que resuelve, en palabras del owner: «Blanco, blanco, vlanco».
// Son dos problemas distintos con dos soluciones distintas:
//
//   • «Blanco» / «blanco»: el mismo valor escrito distinto. Lo unifica el
//     backend al guardar, sin preguntar (utils/etiquetaTexto.js), y el
//     desplegable ya no ofrece las tres variantes como si fueran tres cosas.
//   • «vlanco»: un dedazo. Nada en el texto permite distinguirlo de una
//     palabra nueva de verdad, así que corregirlo solo sería adivinar sobre
//     datos ajenos. Se AVISA acá y se corrige en un click.
//
// Se usa dentro de un `<Form.Item>`: AntD inyecta `value` y `onChange`, y este
// componente los reenvía tal cual.
import React, { useMemo } from "react";
import { AutoComplete, Input } from "antd";
import { BulbOutlined } from "@ant-design/icons";
import { fuzzyIncludes } from "../../../utils/fuzzyMatch";
import { parecidosA } from "../../../utils/textoSimilar";
import "../inventario.css";

interface Props {
  /** Inyectado por Form.Item. */
  value?: string;
  /** Inyectado por Form.Item. */
  onChange?: (valor: string) => void;
  /**
   * Inyectado por Form.Item para el `htmlFor` de su label. Hay que reenviarlo
   * al control real: si se queda acá, el label deja de enfocar el campo al
   * hacerle click y se rompe la lectura por lector de pantalla.
   */
  id?: string;
  /** Valores ya usados en esta columna, ya deduplicados por el backend. */
  opciones?: string[];
  placeholder?: string;
}

const CampoEtiqueta: React.FC<Props> = ({
  value,
  onChange,
  id,
  opciones,
  placeholder,
}) => {
  const parecidos = useMemo(() => parecidosA(value, opciones), [value, opciones]);

  const campo = opciones?.length ? (
    <AutoComplete
      id={id}
      value={value}
      onChange={onChange}
      options={opciones.map((v) => ({ value: v }))}
      filterOption={(input, option) => fuzzyIncludes(option?.value, input)}
      placeholder={placeholder}
    />
  ) : (
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
    />
  );

  return (
    <div className="inventario-campo-etiqueta">
      {campo}
      {parecidos.length > 0 && (
        <div className="inventario-etiqueta-aviso">
          <BulbOutlined />
          <span>
            ¿Quisiste decir{" "}
            {parecidos.map((p, i) => (
              <React.Fragment key={p}>
                {i > 0 && " o "}
                {/* Botón, no enlace: no navega a ninguna parte. Un <a> acá
                    obliga a un href falso y rompe la navegación por teclado. */}
                <button
                  type="button"
                  className="inventario-etiqueta-aviso-opcion"
                  onClick={() => onChange?.(p)}
                >
                  {p}
                </button>
              </React.Fragment>
            ))}
            ?
          </span>
        </div>
      )}
    </div>
  );
};

export default CampoEtiqueta;
